import { useEffect, useState } from "react";
import {
  User,
  Bell,
  Shield,
  Eye,
  BookOpen,
  Monitor,
  Trash2,
  Save,
  Loader,
  KeyRound,
  X,
  Smartphone,
  Globe,
  Lock,
  AlertCircle,
  CheckCircle,
  LogOut,
  Settings as SettingsIcon,
  Moon,
  Sun,
  Laptop,
  TrendingUp,
  Award,
  ChevronDown,
  MessageCircle,
  Mail,
  MapPin,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  userSettingsApi,
  authApi,
  UserSettings,
  showSuccess,
  showError,
} from "../lib";

const tabs = [
  {
    id: "privacy",
    label: "Privacy",
    icon: Eye,
    description: "Control who sees your data",
  },
  {
    id: "security",
    label: "Security",
    icon: Shield,
    description: "Protect your account",
  },
  {
    id: "accessibility",
    label: "Accessibility",
    icon: Monitor,
    description: "Visual preferences",
  },
];

const UserSettingsPage = () => {
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState("privacy");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [show2FACodeInput, setShow2FACodeInput] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disable2FAPassword, setDisable2FAPassword] = useState("");
  const [activeSessions, setActiveSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [sending2FA, setSending2FA] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchActiveSessions();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const result = await userSettingsApi.getSettings();
      if (result.success) {
        setSettings(result.data);
      }
    } catch (err: any) {
      console.error("Failed to load settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const result = await authApi.getActiveSessions();
      if (result.success) {
        setActiveSessions(result.data);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    }
  };

  const updateSettings = async (section: string, data: any) => {
    setSaving(true);
    try {
      let result;
      switch (section) {
        case "privacy":
          result = await userSettingsApi.updatePrivacy(data);
          break;
        case "security":
          result = await userSettingsApi.updateSecurity(data);
          break;
        case "accessibility":
          result = await userSettingsApi.updateAccessibility(data);
          break;
        default:
          result = await userSettingsApi.updateSettings(data);
      }
      if (result.success) {
        showSuccess("Settings saved successfully");
        fetchSettings();
      }
    } catch (err: any) {
      showError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      showError("Please enter your password");
      return;
    }
    setSaving(true);
    try {
      const result = await userSettingsApi.deleteAccount(deletePassword);
      if (result.success) {
        showSuccess("Account deleted");
        logout();
      }
    } catch (err: any) {
      showError(err.message || "Failed to delete account");
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
      setDeletePassword("");
    }
  };

  const handleEnable2FA = async () => {
    try {
      setSending2FA(true);
      const result = await authApi.enable2FA();
      if (result.success) {
        showSuccess("Verification code sent to your email");
        setShow2FACodeInput(true);
      }
    } catch (err: any) {
      showError(err.message || "Failed to send verification code");
    } finally {
      setSending2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      showError("Please enter the 6-digit verification code");
      return;
    }
    try {
      setSaving(true);
      const result = await authApi.verify2FA(twoFACode);
      if (result.success) {
        showSuccess("2FA enabled successfully");
        setShow2FACodeInput(false);
        setTwoFACode("");
        setSettings({
          ...settings,
          security: { ...settings?.security, twoFactorEnabled: true },
        });
        await userSettingsApi.updateSecurity({ twoFactorEnabled: true });
      }
    } catch (err: any) {
      showError(err.message || "Invalid verification code");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disable2FAPassword) {
      showError("Please enter your password to disable 2FA");
      return;
    }
    try {
      setSaving(true);
      const result = await authApi.disable2FA(disable2FAPassword);
      if (result.success) {
        showSuccess("2FA disabled successfully");
        setShowDisable2FAModal(false);
        setDisable2FAPassword("");
        setSettings({
          ...settings,
          security: { ...settings?.security, twoFactorEnabled: false },
        });
        await userSettingsApi.updateSecurity({ twoFactorEnabled: false });
      }
    } catch (err: any) {
      showError(err.message || "Failed to disable 2FA");
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      const result = await authApi.revokeSession(sessionId);
      if (result.success) {
        showSuccess("Session revoked");
        fetchActiveSessions();
      }
    } catch (err: any) {
      showError(err.message || "Failed to revoke session");
    }
  };

  const renderPrivacySettings = () => {
    const privacy = settings?.privacy || {};
    return (
      <div className="space-y-5">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Privacy Control
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage who can see your information and activity
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              Profile Visibility
            </label>
            <select
              value={privacy.profileVisibility || "students"}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  privacy: {
                    ...privacy,
                    profileVisibility: e.target.value as any,
                  },
                })
              }
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 bg-white/50"
            >
              <option value="public">🌍 Public - Everyone</option>
              <option value="students">👨‍🎓 Students Only</option>
              <option value="connections">🔗 Connections Only</option>
              <option value="private">🔒 Private - Only you</option>
            </select>
          </div>

          <div className="space-y-3">
            {[
              {
                key: "showProgress",
                label: "Show course progress",
                icon: BookOpen,
              },
              {
                key: "showCertificates",
                label: "Show certificates",
                icon: Award,
              },
              {
                key: "allowMessaging",
                label: "Allow messaging",
                icon: MessageCircle,
              },
              {
                key: "showInLeaderboard",
                label: "Show in leaderboard",
                icon: TrendingUp,
              },
            ].map((item) => (
              <label
                key={item.key}
                className="flex items-center justify-between p-3 transition-colors cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-white rounded-lg">
                    <item.icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-700">{item.label}</span>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={privacy[item.key as keyof typeof privacy] ?? true}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        privacy: { ...privacy, [item.key]: e.target.checked },
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 transition-colors bg-gray-300 rounded-full peer peer-checked:bg-orange-500"></div>
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={() => updateSettings("privacy", settings?.privacy)}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/25 transition-all disabled:opacity-50 group"
        >
          {saving ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Privacy Settings
            </>
          )}
        </button>
      </div>
    );
  };

  const renderSecuritySettings = () => {
    const security = settings?.security || {};
    return (
      <div className="space-y-5">
        <div
          className={`p-4 rounded-xl border ${
            security.twoFactorEnabled
              ? "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-100"
              : "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-100"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Lock
                className={`w-5 h-5 ${
                  security.twoFactorEnabled
                    ? "text-emerald-600"
                    : "text-amber-600"
                }`}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900">
                Security Status
              </h3>
              <p className="text-xs flex items-center gap-1 mt-0.5">
                {security.twoFactorEnabled ? (
                  <span className="text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> 2FA Enabled - Your
                    account is secure
                  </span>
                ) : (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> 2FA Disabled -
                    Recommended for better security
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-200 transition-all">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <KeyRound className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-xs text-gray-500">
                    Add an extra layer of security
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={security.twoFactorEnabled ?? false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleEnable2FA();
                    } else {
                      setShowDisable2FAModal(true);
                    }
                  }}
                  className="sr-only peer"
                />
                <div
                  className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                    security.twoFactorEnabled ? "bg-orange-500" : "bg-gray-300"
                  }`}
                ></div>
              </label>
            </div>

            {show2FACodeInput && (
              <div className="mt-4 p-3 border border-blue-200 rounded-lg bg-blue-50">
                <p className="mb-2 text-sm text-blue-800">
                  Enter the 6-digit code sent to your email:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) =>
                      setTwoFACode(
                        e.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    placeholder="123456"
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                    maxLength={6}
                  />
                  <button
                    onClick={handleVerify2FA}
                    disabled={saving || twoFACode.length !== 6}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      "Verify"
                    )}
                  </button>
                </div>
              </div>
            )}

            {sending2FA && (
              <div className="mt-3 p-3 text-center bg-blue-50 rounded-lg">
                <Loader className="w-4 h-4 mx-auto text-blue-600 animate-spin" />
                <p className="text-xs text-blue-600 mt-1">
                  Sending verification code...
                </p>
              </div>
            )}
          </div>

          <div className="p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-200 transition-all">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <Bell className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Login Alerts
                  </h4>
                  <p className="text-xs text-gray-500">
                    Get notified on new device logins
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={security.loginAlerts ?? true}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      security: { ...security, loginAlerts: e.target.checked },
                    })
                  }
                  className="sr-only peer"
                />
                <div
                  className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                    security.loginAlerts ? "bg-orange-500" : "bg-gray-300"
                  }`}
                ></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-200 transition-all">
            <label className="block mb-2 text-sm font-semibold text-gray-900">
              Session Timeout
            </label>
            <select
              value={security.sessionTimeout || 30}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  security: {
                    ...security,
                    sessionTimeout: parseInt(e.target.value),
                  },
                })
              }
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 bg-white/50"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="480">8 hours</option>
            </select>
          </div>

          <div>
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="flex items-center justify-between w-full p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-300 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <Monitor className="w-5 h-5 text-gray-700" />
                </div>
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Active Sessions
                  </h4>
                  <p className="text-xs text-gray-500">
                    Manage your logged-in devices
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${showSessions ? "rotate-180" : ""}`}
              />
            </button>

            {showSessions && (
              <div className="mt-3 space-y-2">
                {activeSessions.length > 0 ? (
                  activeSessions.map((session: any) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {session.device || "Unknown Device"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {session.location || "Unknown Location"} •{" "}
                          {session.lastActive || "Recently active"}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRevokeSession(session.id)}
                        className="text-xs text-red-500 hover:text-red-600 font-medium"
                      >
                        Revoke
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-3">
                    No active sessions found
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => updateSettings("security", settings?.security)}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/25 transition-all disabled:opacity-50 group"
        >
          {saving ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Security Settings
            </>
          )}
        </button>
      </div>
    );
  };

  const renderAccessibilitySettings = () => {
    const accessibility = settings?.accessibility || {};
    return (
      <div className="space-y-5">
        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Monitor className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Display Preferences
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Customize how content appears on your screen
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-white border border-gray-200 rounded-xl">
            <label className="block mb-2 text-sm font-semibold text-gray-900">
              Font Size
            </label>
            <select
              value={accessibility.fontSize || "medium"}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  accessibility: {
                    ...accessibility,
                    fontSize: e.target.value as any,
                  },
                })
              }
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 bg-white/50"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
            <div className="mt-3 p-3 rounded-xl bg-gray-50">
              <p
                className={`text-gray-600 ${
                  accessibility.fontSize === "small"
                    ? "text-xs"
                    : accessibility.fontSize === "large"
                      ? "text-base"
                      : "text-sm"
                }`}
              >
                Preview text: "Learning is a treasure that will follow its owner
                everywhere."
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {[
              {
                key: "highContrast",
                label: "High contrast mode",
                icon: Sun,
                description: "Better color contrast for readability",
              },
              {
                key: "screenReaderOptimized",
                label: "Screen reader optimized",
                icon: Smartphone,
                description: "Optimized layout for screen readers",
              },
            ].map((item) => (
              <label
                key={item.key}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-orange-300 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <item.icon className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-gray-900">
                      {item.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {item.description}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={
                      accessibility[item.key as keyof typeof accessibility] ??
                      false
                    }
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        accessibility: {
                          ...accessibility,
                          [item.key]: e.target.checked,
                        },
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 transition-colors bg-gray-300 rounded-full peer peer-checked:bg-orange-500"></div>
                  <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={() =>
            updateSettings("accessibility", settings?.accessibility)
          }
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/25 transition-all disabled:opacity-50 group"
        >
          {saving ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Accessibility Settings
            </>
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-8">
      <div className="px-4 mx-auto max-w-6xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-amber-600 to-orange-500 shadow-md">
              <SettingsIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
                Settings
              </h1>
              <p className="text-sm text-gray-500">
                Manage your privacy and security preferences
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar */}
          <div className="flex-shrink-0 lg:w-72">
            <div className="sticky space-y-4 top-20">
              <div className="p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-100">
                  <div className="flex items-center justify-center w-12 h-12 text-lg font-bold text-white rounded-xl bg-gradient-to-br from-orange-600 to-orange-500 shadow-md">
                    {user?.firstName?.charAt(0) ||
                      user?.email?.charAt(0) ||
                      "U"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                        activeTab === tab.id
                          ? "bg-gradient-to-r from-orange-50 to-amber-50 text-orange-600 border-l-2 border-orange-500"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{tab.label}</p>
                        <p className="text-[10px] text-gray-400">
                          {tab.description}
                        </p>
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center w-full gap-3 px-3 py-2.5 mt-2 text-red-600 rounded-xl hover:bg-red-50 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">Delete Account</p>
                      <p className="text-[10px] text-red-400">
                        Permanently remove
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <h2 className="text-lg font-bold text-gray-900">
                  {tabs.find((t) => t.id === activeTab)?.label || "Settings"}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tabs.find((t) => t.id === activeTab)?.description ||
                    "Manage your preferences"}
                </p>
              </div>
              <div className="p-6">
                {activeTab === "privacy" && renderPrivacySettings()}
                {activeTab === "security" && renderSecuritySettings()}
                {activeTab === "accessibility" && renderAccessibilitySettings()}
              </div>
            </div>

            {/* Logout Button */}
            <div className="mt-4">
              <button
                onClick={() => logout()}
                className="flex items-center justify-center w-full gap-2 py-2.5 text-sm font-medium text-gray-700 transition-all bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Disable 2FA Modal */}
      {showDisable2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-yellow-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <KeyRound className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Disable 2FA
                  </h3>
                  <p className="text-sm text-gray-500">
                    Two-factor authentication will be turned off
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                Enter your password to confirm disabling two-factor
                authentication.
              </p>
              <input
                type="password"
                value={disable2FAPassword}
                onChange={(e) => setDisable2FAPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDisable2FAModal(false);
                    setDisable2FAPassword("");
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisable2FA}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50 transition"
                >
                  {saving ? (
                    <Loader className="w-4 h-4 mx-auto animate-spin" />
                  ) : (
                    "Disable 2FA"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-red-50 to-rose-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete Account
                  </h3>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                Type your password to confirm account deletion. All your data
                will be permanently removed.
              </p>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword("");
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition"
                >
                  {saving ? (
                    <Loader className="w-4 h-4 mx-auto animate-spin" />
                  ) : (
                    "Delete Account"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default UserSettingsPage;
